/**
 * GENERATED — do not edit. Run `node ci/gen_schemas.mjs` instead.
 *
 * Source: catalogs/peterhof/v1/catalog.json (protocol v1.0)
 * catalogId: https://peterhof.aiagentslab.ru/catalogs/peterhof/v1/catalog.json
 *
 * The catalogue is generated on the backend from
 * `graphs/peterhof_canvas/components.py`, so these schemas are derived from
 * the same models the board reducer validates against — the frontend and the
 * backend cannot disagree about a shape without a drift test going red.
 *
 * Two conventions this file inherits from the generator:
 *
 *  * **Unknown keys are STRIPPED, not rejected** (zod default, matching the
 *    backend's `extra="ignore"`). The catalogue's `additionalProperties:false`
 *    is enforced by the drift test; at runtime a field somebody added upstream
 *    must not blank a card for a client that has not redeployed yet.
 *  * **Optional fields accept `null` and yield `undefined`.** The wire says
 *    «absent means absent», but a null once travelled on an op frame while the
 *    snapshot omitted the key — one frame apart, the same field, and a valid
 *    card went stale. One `??` is the belt over that whole class.
 */

import { z } from 'zod';

/** `X | None` on the backend: absent, or null, both mean absent here. */
const optional = <T extends z.ZodTypeAny>(inner: T) =>
  inner.nullish().transform((value) => (value ?? undefined) as z.infer<T> | undefined);

/**
 * `common_types.json#/$defs/ChildList` — a static list of child ids, or a
 * template over the data model. We only ever emit the first form; the second
 * is accepted because the protocol has it and a renderer that rejects a legal
 * message is a renderer that is wrong.
 */
export const childList = z.union([
  z.array(z.string()),
  z.object({ componentId: z.string(), path: z.string() }),
]);

/** `common_types.json#/$defs/ComponentCommon` — the envelope every node has. */
const common = {
  id: z.string().min(1),
  catalogId: optional(z.string()),
  accessibility: optional(z.record(z.string(), z.unknown())),
  metadata: optional(z.record(z.string(), z.unknown())),
};

export const audioPlayerSchema = z.object({
  ...common,
  "chapters": z.array(z.object({ "lat": optional(z.number()), "length_s": optional(z.number().int()), "lon": optional(z.number()), "n": z.number().int(), "src": z.string(), "subtitle": optional(z.string().max(160)), "title": z.string().max(120) })).min(1).max(60),
  "component": z.literal("AudioPlayer"),
  "current": optional(z.number().int()),
  "title": optional(z.string().max(120)),
  "tour": z.string().max(64),
});

/**
 * One layout region, and it DESCRIBES ITSELF. A `Column` with the meaning
 * hidden in its id (`region:main`) would be unreadable to a renderer that
 * has only the catalog. `region` is a HINT — two columns on a wide screen,
 * one on a phone, `footer` drawn in the chat strip — and what is hard is
 * that the hint has to survive to somebody who never read our design.
 */
export const boardRegionSchema = z.object({
  ...common,
  "children": optional(childList),
  "component": z.literal("BoardRegion"),
  "region": z.enum(["header", "main", "side", "footer"]),
});

/**
 * The root of the board. Holds the four regions and the guide's pointer.
 * `focusedId` is a PROP rather than a renderer call on purpose. A
 * `callRendererFunction` would be the obvious spelling of «highlight this
 * card» and it drags a mandatory `rendererFunctionResponse` behind it — and
 * our reverse channel is a whole new run, which is far too much to pay for a
 * highlight. As a prop the focus also becomes STATE: «the card the guide
 * pointed at last» survives a reload, where a transient effect would not.
 */
export const boardSurfaceSchema = z.object({
  ...common,
  "children": optional(childList),
  "component": z.literal("BoardSurface"),
  "focusedId": optional(z.string().describe("Id of the component the guide pointed at last, if any.")),
});

/**
 * A tappable answer. `action.event.userMessage` IS the visitor's next
 * message. That sentence used to be an agreement between `Chips.tsx` and
 * `tools.py`; on this wire it is DECLARED — «An optional human-readable
 * message describing the action performed by the user» is the spec's own
 * field, and a renderer that has never seen our code sends the right string.
 */
export const buttonSchema = z.object({
  ...common,
  "action": z.record(z.string(), z.unknown()).describe("`{event: {name, userMessage}}` — the utterance a tap sends, byte for byte."),
  "component": z.literal("Button"),
  "emoji": optional(z.string().max(8)),
  "label": z.string().max(60),
});

/**
 * Chips. Rides in ``footer`` so a frontend draws them in the CHAT strip
 * under the last reply rather than among cards that accumulate for weeks — a
 * tap is the visitor's next utterance and belongs beside the conversation.
 * Only the LAST block is tappable: on the board that is structural (the
 * fixed id `answers:turn` replaces the previous one), and in a chat
 * scrollback the frontend must dim the older ones — a tap on a
 * three-turn-old chip answers a question nobody is asking any more, which is
 * where BUG22 came from.
 */
export const chipGroupSchema = z.object({
  ...common,
  "children": optional(childList),
  "component": z.literal("ChipGroup"),
  "title": optional(z.string().max(200)),
});

export const eventsListSchema = z.object({
  ...common,
  "actions": optional(z.array(z.object({ "emoji": optional(z.string().max(8)), "label": z.string().max(60), "value": z.string().max(200) }).describe("A tappable option. ``value`` is LOAD-BEARING: a tap sends it as the\nvisitor's next message, so it is never rewritten, completed or auto-sent.")).max(4)),
  "component": z.literal("EventsList"),
  "highlight": optional(z.array(z.string()).max(20)),
  "items": z.array(z.object({ "date_label": optional(z.string().max(120).describe("The museum's OWN date line («14 августа, 19:00»), to be printed as given. It is a live string: reformatting or declining one by rule is how a renderer starts saying something the source did not.")), "image": optional(z.string()), "key": optional(z.string().max(64)), "title": z.string().max(160), "url": optional(z.string()), "value": optional(z.string().max(200)), "venue": optional(z.string().max(120)) })).min(1).max(12),
  "title": optional(z.string().max(120)),
});

/**
 * A chip that opens a page instead of speaking. Its own component rather
 * than a `url` prop on `Button`: a catalog component is validated with
 * `unevaluatedProperties: false`, so a prop nobody declared is a rejected
 * card, and «sends a message» and «opens a link» are two different
 * affordances anyway.
 */
export const linkButtonSchema = z.object({
  ...common,
  "component": z.literal("LinkButton"),
  "emoji": optional(z.string().max(8)),
  "label": z.string().max(60),
  "url": z.string(),
});

/**
 * **No raster, ever.** `dgis.static_map_url` bakes the 2GIS api key into the
 * URL, and in a browser that is DevTools — so the canvas surface drops the
 * `map_image` attachment whole (which also keeps the key out of
 * `values.attachments`, where no nginx trick could reach it) and ships pins
 * plus a KEYLESS deep link. `docs/UI_CONTRACT.md` allows exactly this: «a
 * frontend that can draw an interactive map instead may ignore the
 * attachment». A missing `route` is NORMAL: the Routing quota runs out long
 * before the static-map one, and a map with pins and no line is a working
 * map.
 */
export const mapCardSchema = z.object({
  ...common,
  "center": z.object({ "lat": z.number(), "lon": z.number() }),
  "component": z.literal("MapCard"),
  "current": optional(z.number().int().min(1).describe("`n` of the pin the visitor is walking towards NOW. Everything numbered below it has been reached; there is no second «visited» list to disagree with this one.")),
  "dgis_url": optional(z.string()),
  "legs": optional(z.array(z.object({ "distance_m": z.number().int().min(0), "duration_s": optional(z.number().int().min(0)), "first": optional(z.number().int().min(0).describe("Index in `route.points` where this leg begins. Sent because the alternative is a GUESS: a renderer projecting the pin onto the line picks the wrong vertex wherever the walk passes near itself, which in a park of looping alleys is the ordinary case.")), "last": optional(z.number().int().min(0)), "n_from": z.number().int().min(0).describe("`n` of the pin this leg starts at — 0 is the visitor's own position."), "n_to": z.number().int().min(0), "source": z.enum(["dgis", "dgis_split", "cache", "straight"]), "style": optional(z.string().max(40).describe("2GIS's own word for the dominant surface of this leg — «park_path», «stairway», «pedestrian_bridge». Passed through rather than translated: a stairway is the one thing a visitor with a pushchair has to know, and it is the nearest this wire gets to turn-by-turn.")) }).describe("One stretch of the walk: between which two pins, how far, how long.\n\n``source`` is what makes the repo's oldest promise about time checkable\nrather than remembered: **only 2GIS's own minutes may be called exact.**\n`dgis` measured this pair; `dgis_split` read it back out of a multi-point\nroute, so its seconds are a share of the whole and are «примерно»;\n`straight` is a line on the map and a walking pace, which is an estimate\nwith no router in it at all.")).max(23)),
  "mode": optional(z.enum(["card", "nav"]).describe("«nav» means the visitor is BEING LED along this route right now, and a surface that can should give the map the whole screen. Absent reads as «card» — the map is one element among others. A prop rather than a flag in the data model because the mode belongs to the map: one authority, read where it is drawn, and it survives a reload for free.")),
  "pins": z.array(z.object({ "id": z.string().max(64), "kind": z.enum(["sight", "entrance", "break", "arrival", "me"]), "label": z.string().max(80), "lat": z.number(), "lon": z.number(), "n": optional(z.number().int()), "ref": optional(z.string().max(64).describe("The board element this pin STANDS FOR («obj:40»), when there is one. A tap then opens the card the visitor has already been told about instead of asking the model about an object it has just described. Empty when the pin is the visitor themselves or a point with no card of its own.")) })).min(1).max(24),
  "route": optional(z.object({ "points": optional(z.array(z.array(z.number()))) })),
  "total_distance_m": optional(z.number().int().min(0)),
  "total_duration_s": optional(z.number().int().min(0).describe("The WHOLE route's seconds as 2GIS returned them — an aggregate, and exact when it is here.")),
  "zoom": optional(z.number().int().min(10).max(18)),
});

export const noteCardSchema = z.object({
  ...common,
  "component": z.literal("NoteCard"),
  "source": optional(z.string().max(120)),
  "text": z.string().max(600),
  "title": optional(z.string().max(80)),
  "tone": z.enum(["info", "warn", "emergency"]),
});

export const objectCardSchema = z.object({
  ...common,
  "actions": optional(z.array(z.object({ "emoji": optional(z.string().max(8)), "label": z.string().max(60), "value": z.string().max(200) }).describe("A tappable option. ``value`` is LOAD-BEARING: a tap sends it as the\nvisitor's next message, so it is never rewritten, completed or auto-sent.")).max(4)),
  "component": z.literal("ObjectCard"),
  "images": optional(z.array(z.string()).max(6)),
  "kind": optional(z.string().max(40)),
  "lat": optional(z.number()),
  "links": optional(z.array(z.object({ "label": z.string().max(60), "url": z.string() })).max(3)),
  "lon": optional(z.number()),
  "status": optional(z.enum(["open", "closed", "restoration", "dayoff", "unknown"])),
  "status_note": optional(z.string().max(200).describe("The museum's OWN words for a closure, quoted — never composed here. That is what keeps the grammar honest as well as the fact: «закрыт» agrees with «Монплезир» and would have to be «закрыта» for «Ева».")),
  "subtitle": optional(z.string().max(160)),
  "text": optional(z.string().max(1200)),
  "title": z.string().max(120),
});

/**
 * A set of peers. ``layout`` is decided by the BACKEND (`ui.carousel_ready`:
 * two or more options, homogeneous link/tap, a photo on EVERY tile) — by the
 * time this is on the wire the question is answered and the frontend obeys.
 */
export const objectStripSchema = z.object({
  ...common,
  "component": z.literal("ObjectStrip"),
  "layout": z.enum(["strip", "column"]),
  "tiles": z.array(z.object({ "id": z.string().max(64), "image": optional(z.string()), "label": z.string().max(80), "sublabel": optional(z.string().max(80)), "url": optional(z.string()), "value": optional(z.string().max(200)) })).min(1).max(12),
  "title": optional(z.string().max(120)),
});

/**
 * Ready plans OFFERED to choose from, not yet taken (`peterhof_plan`, #87).
 * Every stop of every plan, materialised on the visit date — the list the
 * model used to retell and got wrong (#78), drawn by the code instead. No
 * choose button on the card: the chat's footer chips («Беру «…»») are the
 * choice, one per plan, and a second set here would say it twice. No
 * template id either — the visitor answers with the number or the title.
 */
export const planOfferSchema = z.object({
  ...common,
  "component": z.literal("PlanOffer"),
  "date": optional(z.string().max(80)),
  "plans": z.array(z.object({ "breakdown": optional(z.string().max(120).describe("What `duration` — the whole visit, «≈ 2 ч 30 мин» — is made of: «на точках 1 ч 35 мин · переходы ≈ 55 мин». Printed under the title as given (`peterhof_plan.plan_time`, INV-PH-113).")), "duration": optional(z.string().max(40)), "images": optional(z.array(z.object({ "caption": optional(z.string().max(120).describe("The stop the photograph shows, as the plan names it.")), "url": z.string().max(300) }).describe("One photograph on an offered plan (#155): a stop of the plan, as our copy of the\nmuseum's photograph (`peterhof_plan.object_photos`, #153).")).max(5)), "n": z.number().int().min(1).describe("The number the offer gave this plan — the one the visitor answers with («давай второй»). Never renumbered by the renderer."), "stops": z.array(z.object({ "note": optional(z.string().max(200).describe("The stop's warning on the visit date as the code wrote it («фонтан не работает: …»), printed as given.")), "title": z.string().max(120) })).min(1).max(16), "summary": optional(z.string().max(300)), "title": z.string().max(120), "warning": optional(z.string().max(200)) })).min(1).max(3),
  "title": optional(z.string().max(120)),
  "weather": optional(z.object({ "date": z.string().max(10).describe("The day the forecast is for, YYYY-MM-DD."), "text": z.string().max(120).describe("«морось, +7…+9 °C, ветер до 21 км/ч» — rounded, Russian, no emoji (`feeds.weather_text`).") }).describe("The visit day's forecast on the offer (#205), as the code words it — printed as given.")),
});

export const priceTableSchema = z.object({
  ...common,
  "actions": optional(z.array(z.object({ "emoji": optional(z.string().max(8)), "label": z.string().max(60), "value": z.string().max(200) }).describe("A tappable option. ``value`` is LOAD-BEARING: a tap sends it as the\nvisitor's next message, so it is never rewritten, completed or auto-sent.")).max(4)),
  "buy_url": optional(z.string()),
  "component": z.literal("PriceTable"),
  "date_label": optional(z.string().max(60)),
  "highlight": optional(z.array(z.string()).max(20)),
  "is_weekend": optional(z.boolean()),
  "notes": optional(z.array(z.string()).max(4)),
  "rows": z.array(z.object({ "key": optional(z.string().max(64)), "note": optional(z.string().max(120)), "price": z.string().min(1).max(60).describe("A STRING with the currency already in it — «1200 ₽», and «бесплатно» when the feed prices the object at nothing. Free is the feed's ANSWER rather than a gap, and a bare 0 on a screen reads as «unknown» to everyone who has ever seen a loading state."), "title": z.string().max(160) })).min(1).max(20),
  "title": optional(z.string().max(120)),
  "weekday_label": optional(z.string().max(20).describe("The DAY this tariff belongs to, meant to be printed beside it. A weekend price applied to a Friday was a real defect here, and naming the day is the cheapest defence there is against reading the wrong column.")),
});

export const scheduleCardSchema = z.object({
  ...common,
  "actions": optional(z.array(z.object({ "emoji": optional(z.string().max(8)), "label": z.string().max(60), "value": z.string().max(200) }).describe("A tappable option. ``value`` is LOAD-BEARING: a tap sends it as the\nvisitor's next message, so it is never rewritten, completed or auto-sent.")).max(4)),
  "component": z.literal("ScheduleCard"),
  "date_label": optional(z.string().max(60)),
  "highlight": optional(z.array(z.string()).max(12)),
  "notice": optional(z.string().max(300).describe("A sanitary day, a closure, the museum's own note — whatever it said about THIS date, quoted rather than composed.")),
  "rows": z.array(z.object({ "closed": optional(z.boolean().describe("The museum has this object SHUT on this date — a sanitary day, a day off, a restoration — and `hours` carries its own words for why. Drawn visibly apart: in a list of a route's stops the closed one is the row a visitor must not miss.")), "hours": z.string().min(1).max(120).describe("The museum's own string («10:00–19:45»), not a parsed pair: it publishes ranges, seasons and «по записи» in one field, so splitting it into open/close throws away every value that is not a range."), "key": optional(z.string().max(64)), "note": optional(z.string().max(200)), "title": z.string().max(120) })).min(1).max(12),
  "weekday_label": optional(z.string().max(20)),
});

/**
 * What a tool's result will look like, before it arrives. **The board's only
 * lifecycle, and it lives here rather than on the envelope.** The old wire
 * carried a `status` on every element, and the backend never sent one other
 * than `ready` on a real card: `loading` and `error` belong to a PENDING
 * call and nothing else, and `stale` was always the renderer's own word for
 * «validation failed, I kept the last good props». So the channel became a
 * prop of the one component it was ever about.
 */
export const skeletonCardSchema = z.object({
  ...common,
  "component": z.literal("SkeletonCard"),
  "hint": optional(z.string().max(80)),
  "state": optional(z.enum(["loading", "error"]).describe("`loading` while the tool is awaited, `error` when its source did not answer.")),
});

/**
 * Garnish under an answer rather than an artefact of the trip — `footer`,
 * like `answers`.
 */
export const sourcesCardSchema = z.object({
  ...common,
  "component": z.literal("SourcesCard"),
  "items": z.array(z.object({ "title": z.string().max(160), "url": z.string() })).min(1).max(3),
});

export const transportCardSchema = z.object({
  ...common,
  "component": z.literal("TransportCard"),
  "direction": z.enum(["to", "from"]),
  "options": z.array(z.object({ "duration": z.string().max(40), "label": z.string().max(120), "mode": z.string().max(40), "note": optional(z.string().max(200)), "url": optional(z.string()) })).min(1).max(8),
});

export const tripPlanSchema = z.object({
  ...common,
  "actions": optional(z.array(z.object({ "emoji": optional(z.string().max(8)), "label": z.string().max(60), "value": z.string().max(200) }).describe("A tappable option. ``value`` is LOAD-BEARING: a tap sends it as the\nvisitor's next message, so it is never rewritten, completed or auto-sent.")).max(4)),
  "as_of": optional(z.string().max(10).describe("The day (ISO, Moscow) the card's facts — closures, marks, minutes — were read for this snapshot. The export prints it as «данные на …»: a saved plan is read days later, offline.")),
  "change_note": optional(z.string().max(200).describe("What changed about this plan on the turn that drew it — «+Эрмитаж (после «Большой каскад»), −Марли». The card is a SNAPSHOT of the whole plan, so without this line a visitor has to diff two lists by eye to see what their own edit did. Written by `peterhof_plan` (the plan tools), absent on a plan that was merely re-drawn and on the board, where the trip is the surface rather than a message.")),
  "component": z.literal("TripPlan"),
  "date": optional(z.string()),
  "dates": optional(z.array(z.string()).max(3).describe("The visit dates `date` says, as ISO `YYYY-MM-DD` — what a calendar event is put on. Written only when they ARE what `date` says; empty when the visitor named no date or named it in words the code could not turn into one, and then there is no calendar entry rather than a guessed day.")),
  "days": z.array(z.object({ "date": optional(z.string()), "items": optional(z.array(z.object({ "from_prev_m": optional(z.number().int()), "hours": optional(z.string().max(60)), "kind": z.enum(["arrival", "entrance", "stop", "break"]), "lat": optional(z.number()), "lon": optional(z.number()), "n": optional(z.number().int().min(1).describe("Only a `stop` is numbered, and the list is NEVER renumbered by the renderer: the numbers belong to the visitor — «идём к третьей точке» is the third SIGHT, not the third row. An arrival, an entrance and a break are unnumbered by construction.")), "note": optional(z.string().max(200)), "status": optional(z.enum(["ok", "closed", "dayoff", "sanitary", "unknown"])), "subtitle": optional(z.string().max(160)), "tickets_url": optional(z.string()), "title": z.string().max(120) }))), "label": optional(z.string().max(80)) })).min(1).max(3),
  "next_visit": optional(z.array(z.object({ "season": optional(z.string().max(60)), "summary": optional(z.string().max(300)), "title": z.string().max(120) }).describe("One ready plan for the NEXT visit (#50): the catalogue's own title and summary,\nand when it can be walked when that is not any day («в сезон фонтанов»).")).max(2).describe("Ready plans of the catalogue for ANOTHER visit, picked by code from the conversation's interests (`peterhof_plan.templates.next_visit`) — never written by a model.")),
  "title": optional(z.string().max(120)),
  "totals": optional(z.object({ "breakdown": optional(z.string().max(120).describe("What `hours` is made of, in the code's words — «на точках 1 ч 35 мин · переходы ≈ 55 мин». Printed as given beside the hours, never recomputed: the client has neither the stops' coordinates nor the file's numbers (`peterhof_plan.plan_time`, INV-PH-113). Absent on a card that has no such split.")), "hours": optional(z.number()), "stops": z.number().int() })),
  "trip_link": optional(z.string().max(480).describe("The way back into the guide from the saved plan (INV-PH-166): an opaque token the CODE wrote (`peterhof_v2.return_link`) — the template's id or the stops' museum ids, the visit day, the party. The client only puts it after `?trip=` in the PDF's and the calendar file's link and QR code; it never reads it, and it holds no thread or visitor id. Absent on a card that cannot be rebuilt (no day) and on the Agent's cards.")),
});

/**
 * Numbers as the FEED said them. `precipitation` and `wind` are sentences
 * rather than a probability and a speed, and that is the wire's own answer
 * to a question this package may not settle: the units are Open-Meteo's, the
 * wind direction is its two Russian letters, and a surface re-deriving «40
 * %» into «возможен дождь» would be a second reader of one measurement. A
 * forecast's range lives in `summary` for the same reason — printing one end
 * of it as «the temperature» is a claim the feed did not make.
 */
export const weatherCardSchema = z.object({
  ...common,
  "actions": optional(z.array(z.object({ "emoji": optional(z.string().max(8)), "label": z.string().max(60), "value": z.string().max(200) }).describe("A tappable option. ``value`` is LOAD-BEARING: a tap sends it as the\nvisitor's next message, so it is never rewritten, completed or auto-sent.")).max(4)),
  "component": z.literal("WeatherCard"),
  "date_label": optional(z.string().max(60)),
  "fountains": optional(z.string().max(120)),
  "precipitation": optional(z.string().max(160)),
  "summary": optional(z.string().max(200)),
  "temperature_c": optional(z.number()),
  "wind": optional(z.string().max(80)),
});

/** Every component the catalogue declares, by its wire name. */
export const CATALOG_SCHEMAS = {
  AudioPlayer: audioPlayerSchema,
  BoardRegion: boardRegionSchema,
  BoardSurface: boardSurfaceSchema,
  Button: buttonSchema,
  ChipGroup: chipGroupSchema,
  EventsList: eventsListSchema,
  LinkButton: linkButtonSchema,
  MapCard: mapCardSchema,
  NoteCard: noteCardSchema,
  ObjectCard: objectCardSchema,
  ObjectStrip: objectStripSchema,
  PlanOffer: planOfferSchema,
  PriceTable: priceTableSchema,
  ScheduleCard: scheduleCardSchema,
  SkeletonCard: skeletonCardSchema,
  SourcesCard: sourcesCardSchema,
  TransportCard: transportCardSchema,
  TripPlan: tripPlanSchema,
  WeatherCard: weatherCardSchema,
} as const;

export type CatalogComponentName = keyof typeof CATALOG_SCHEMAS;

/** The catalogue this file was generated from — pinned by the drift test. */
export const CATALOG_ID = "https://peterhof.aiagentslab.ru/catalogs/peterhof/v1/catalog.json";
export const PROTOCOL_VERSION = "1.0";

/** Which children a container may hold — the catalogue's own answer. */
export const ALLOWED_CHILDREN: Record<string, readonly string[]> = {
  BoardRegion: ["AudioPlayer", "ChipGroup", "EventsList", "MapCard", "NoteCard", "ObjectCard", "ObjectStrip", "PlanOffer", "PriceTable", "ScheduleCard", "SkeletonCard", "SourcesCard", "TransportCard", "TripPlan", "WeatherCard"],
  BoardSurface: ["BoardRegion"],
  ChipGroup: ["Button", "LinkButton"],
};

export type AudioPlayerProps = z.infer<typeof audioPlayerSchema>;
export type BoardRegionProps = z.infer<typeof boardRegionSchema>;
export type BoardSurfaceProps = z.infer<typeof boardSurfaceSchema>;
export type ButtonProps = z.infer<typeof buttonSchema>;
export type ChipGroupProps = z.infer<typeof chipGroupSchema>;
export type EventsListProps = z.infer<typeof eventsListSchema>;
export type LinkButtonProps = z.infer<typeof linkButtonSchema>;
export type MapCardProps = z.infer<typeof mapCardSchema>;
export type NoteCardProps = z.infer<typeof noteCardSchema>;
export type ObjectCardProps = z.infer<typeof objectCardSchema>;
export type ObjectStripProps = z.infer<typeof objectStripSchema>;
export type PlanOfferProps = z.infer<typeof planOfferSchema>;
export type PriceTableProps = z.infer<typeof priceTableSchema>;
export type ScheduleCardProps = z.infer<typeof scheduleCardSchema>;
export type SkeletonCardProps = z.infer<typeof skeletonCardSchema>;
export type SourcesCardProps = z.infer<typeof sourcesCardSchema>;
export type TransportCardProps = z.infer<typeof transportCardSchema>;
export type TripPlanProps = z.infer<typeof tripPlanSchema>;
export type WeatherCardProps = z.infer<typeof weatherCardSchema>;
